/**
 * dview:sections on a group: "key=section" splits the group's rows (on its topic page) or its tiles
 * (the objects on a track or in a structure) under the groups whose dview:key the section names.
 */

import { expandUri } from '@utils/prefix.utils';
import { pickLang } from '@utils/viewShapes.utils';
import type { WidgetContext } from './types';
import styles from './widgets.module.scss';

export interface Section {
  key: string;
  title: string;
  order: number;
}

/** Key → section key; a prefixed key (railml3:Balise, era:gaugingProfile) is expanded to its IRI. */
export function sectionMap(context: WidgetContext, groupId: string | null | undefined): Map<string, string> {
  const group = groupId ? context.model?.shapes?.groups.get(groupId) : undefined;
  return new Map(
    (group?.settings.sections ?? []).map((entry) => {
      const [key, section] = entry.split('=');
      return [key.includes(':') ? expandUri(key, context.prefixes) : key, section] as const;
    })
  );
}

/** The sections the keys fall in, in the order of their groups; what no entry names last, as `other`. */
export function sectionsFor(context: WidgetContext, keys: string[], other: string): Section[] {
  const groups = [...(context.model?.shapes?.groups.values() ?? [])];
  return [...new Set(keys)]
    .map((key) => {
      const group = key ? groups.find((candidate) => candidate.settings.key?.[0] === key) : undefined;
      return {
        key,
        title: group ? (pickLang(group.label, context.language) ?? key) : other,
        order: group?.order ?? Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((a, b) => a.order - b.order);
}

/** On the overview: each section and how many objects it holds, one line each. */
export function SectionSummary({ sections }: { sections: (Section & { count: number })[] }): JSX.Element {
  return (
    <ul className={styles.sectionSummary}>
      {sections.map((section) => (
        <li key={section.key}>
          <span>{section.title}</span>
          <span className={styles.sectionCount}>{section.count}</span>
        </li>
      ))}
    </ul>
  );
}
